import type { RsvpStatus } from '../../shared/useRsvp';

/**
 * Back up and restore: the private window's safety net. Everything the demo
 * knows about a user is two lists in localStorage, so a backup is those two
 * lists for one city, as a file or as a string to paste somewhere safe.
 */

export interface Backup {
  version: 1;
  city: string;
  rsvp: Record<string, RsvpStatus>;
  hidden: string[];
}

/** Marks the pasteable form, so a stray paste is told apart from a backup. */
const PREFIX = 'cg1:';

export function makeBackup(city: string, rsvp: Record<string, RsvpStatus>, hidden: Iterable<string>): Backup {
  return { version: 1, city, rsvp: { ...rsvp }, hidden: [...hidden] };
}

export const backupJson = (backup: Backup): string => JSON.stringify(backup, null, 2);

/** One line, safe to paste into a chat or a note: base64url of the JSON. */
export function encodeBackup(backup: Backup): string {
  const bytes = new TextEncoder().encode(JSON.stringify(backup));
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return PREFIX + btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decode(text: string): unknown {
  const body = text.slice(PREFIX.length).replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(body + '='.repeat((4 - (body.length % 4)) % 4));
  return JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))));
}

/** A pasted string or a file's contents, in either form; null for anything that isn't a backup. */
export function parseBackup(text: string): Backup | null {
  const trimmed = text.trim();
  let raw: unknown;
  try {
    raw = trimmed.startsWith(PREFIX) ? decode(trimmed) : JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const { version, city, rsvp, hidden } = raw as Record<string, unknown>;
  if (version !== 1 || typeof city !== 'string' || !rsvp || typeof rsvp !== 'object' || Array.isArray(rsvp)) {
    return null;
  }
  const clean: Record<string, RsvpStatus> = {};
  for (const [id, status] of Object.entries(rsvp)) {
    if (status === 'going' || status === 'maybe') clean[id] = status;
  }
  return {
    version: 1,
    city,
    rsvp: clean,
    hidden: Array.isArray(hidden) ? hidden.filter((v): v is string => typeof v === 'string') : [],
  };
}

export interface Merged {
  rsvp: Record<string, RsvpStatus>;
  hidden: string[];
  /** RSVPs the backup brought that weren't here: what the toast counts. */
  added: number;
}

/**
 * A backup merged over what this browser already has, by id. What is here
 * wins: a restore fills gaps rather than undoing what was done since. An event
 * on the schedule is never also hidden.
 */
export function mergeBackup(current: { rsvp: Record<string, RsvpStatus>; hidden: Iterable<string> }, incoming: Backup): Merged {
  const rsvp = { ...current.rsvp };
  let added = 0;
  for (const [id, status] of Object.entries(incoming.rsvp)) {
    if (rsvp[id]) continue;
    rsvp[id] = status;
    added += 1;
  }
  const hidden = [...new Set([...current.hidden, ...incoming.hidden])].filter((id) => !rsvp[id]);
  return { rsvp, hidden, added };
}
